import styled, { css } from 'styled-components';
import dayjs from 'dayjs';
import React, { Fragment } from 'react';
import sortBy from 'lodash/sortBy';

import { formatHandicap, removePipes, withRootTestId } from '@williamhill/rosi-ui-shared/src/lib';
import SGPLegsComponent from './sgp-legs-component';
import SuperParlayLegsComponent from './super-parlay-legs-component';
import { ResultType } from '@williamhill/rosi-ui-shared/src/types';
import { Block, Text } from '@williamhill/rosi-ui-shared/src/components/typography';
import { PriceComponent } from './price-component';
import { BetLeg, PriceOdds } from '../../model';
import { ResultIcon } from './result-icon';

export type Leg = {
  id: string;
  event: { name: string };
  eventStartTime: string;
  market: { name: string };
  marketLine: number | null;
  price: PriceOdds | null;
  originalPrice?: PriceOdds | null;
  result: { type: ResultType } | null;
  selection: { name: string };
};

export default function LegsComponent({
  legs,
  betLegs,
  isSGP
}: {
  legs: Leg[];
  betLegs: BetLeg[] | undefined;
  isSGP: boolean;
}) {
  return (
    <>
      {isSGP ? (
        <SGPLegsComponent {...{ legs }} />
      ) : (
        <>
          {betLegs ? (
            <SuperParlayLegsComponent {...{ legs, betLegs }} />
          ) : (
            <>
              <hr />
              <LegsList as="ul" weight="medium">
                {sortBy(legs, ({ eventStartTime }) => new Date(eventStartTime).getTime()).map(
                  (
                    { id, event, eventStartTime, market, selection, price, originalPrice, result, marketLine },
                    index
                  ) => {
                    const withTestId = withRootTestId(`bet-tracker-bets-ticket-leg-${id}`);
                    const selectionName = removePipes(selection.name);
                    const isInjuryProtected = result?.type === 'injury_protection';
                    const isVoid = result?.type === 'void';
                    const isStruckThrough = isVoid || isInjuryProtected;
                    return (
                      <Fragment key={id}>
                        <LegItem data-testid={withTestId()}>
                          <ResultIcon resultType={result?.type} />
                          <LegSection>
                            <header>
                              <SelectionNameContainer
                                data-testid={withTestId('name')}
                                isStruckThrough={isStruckThrough}>
                                {selectionName}
                                {marketLine && ` ${formatHandicap(marketLine, selectionName, false)}`}
                              </SelectionNameContainer>
                              <PriceComponent
                                price={price}
                                originalPrice={originalPrice}
                                isVoided={isVoid || isInjuryProtected}
                              />
                            </header>
                            <main>
                              <MarketNameContainer data-testid={withTestId('market')} isStruckThrough={isStruckThrough}>
                                {removePipes(market.name)}
                              </MarketNameContainer>
                              <EventDateContainer isStruckThrough={isStruckThrough}>
                                {dayjs(eventStartTime).format('MMM DD, h:mma')} |{' '}
                                <EventNameSpan data-testid={withTestId('event-name')}>
                                  {removePipes(event.name)}
                                </EventNameSpan>
                              </EventDateContainer>
                            </main>
                          </LegSection>
                        </LegItem>
                        {legs.length - 1 !== index && <LineBreak />}
                      </Fragment>
                    );
                  }
                )}
              </LegsList>
            </>
          )}
        </>
      )}
    </>
  );
}

const LegsList = styled(Text)`
  position: relative;
  display: flex;
  flex-direction: column;
  list-style-type: none;
  margin: 0;
  padding: 0.8rem 0;
`;

const LineBreak = styled.hr`
  border-style: none;
  border-top: 1px solid ${({ theme }) => theme.colors.grey200};
  height: 1px;
  margin: 0.8rem 0;
`;

const LegItem = styled.li<{ icon?: { color: string; content: string } }>`
  display: flex;
  gap: 1rem;
`;

export const LegSection = styled.section`
  flex-grow: 1;
  & > header {
    display: flex;
  }
`;

export const struckThroughStyles = css`
  text-decoration: line-through;
  text-decoration-thickness: 1px;
  color: ${({ theme }) => theme.colors.neutral};
`;

export const SelectionNameContainer = styled(Block).attrs({ weight: 'bold' })<{ isStruckThrough?: boolean }>`
  flex-grow: 1;
  ${({ isStruckThrough }) => isStruckThrough && struckThroughStyles}
`;

export const MarketNameContainer = styled(Block).attrs({ weight: 'regular' })<{ isStruckThrough?: boolean }>`
  color: ${({ theme }) => theme.colors.brand.quaternary};
  ${({ isStruckThrough }) => isStruckThrough && struckThroughStyles}
`;

export const EventNameSpan = styled.span`
  color: ${({ theme }) => theme.colors.brand.quaternary};
`;

const EventDateContainer = styled(Text).attrs({ as: 'div', size: 'sm', weight: 'regular' })<{
  isStruckThrough?: boolean;
}>`
  ${({ isStruckThrough }) => isStruckThrough && struckThroughStyles}
`;
